import React from 'react';
import * as d3 from 'd3';

export interface IProps {
    productId: any;
    productTitle: string;
    onProductDetailsRequested?: (productId: number) => void;
    
    onGraphFailedToLoad?: () => void;
    onGraphLoaded?: () => void;
}

export interface IState {}

import './product-relatedness-graph.scss';
import WebUtils from '../../../../services/WebUtils';

(window as any).d3 = d3;

export class ProductRelatednessGraph extends React.Component <IProps, IState> {
    private Tooltip: any;
    private simulation: any;
    private dragDrop: any;
    private root: any;
    private hasBeenInitialized: boolean;
    
    constructor(props: IProps){
        super(props);

        this.getNodeColor = this.getNodeColor.bind(this);
        this.mouseOver = this.mouseOver.bind(this);
        this.mouseOverLegend = this.mouseOverLegend.bind(this);
        this.mouseOut = this.mouseOut.bind(this);
        this.handleRootRef = this.handleRootRef.bind(this);

        this.loadSimulation(this.props.productId, true);
    }

    public componentDidUpdate(prevProps: IProps) {
        if (this.props.productId !== prevProps.productId) {
            this.loadSimulation(this.props.productId, true);
        }
    }

    public render(){
        return <div className="graph-container-root" ref={this.handleRootRef}>
                    <svg className="graph" width="100%" height="230"></svg>
                </div>
    }

    private handleRootRef(root){
        if(root != null && this.root == null){
            this.root = root;
            this.initialize();
        }
        
        this.root = root;
    }
    
    private getNodeColor(node, neighbors): string {
        switch (node.level) {
            case 0:
                return "brown";
            case 1:
                return "#02457a";
            case 2:
                return "#018abe";
            case 3:
                return "#d6e8ee"
        }
    }
    
    private mouseOver(event, node) {
        this.Tooltip.style("opacity", 1)
               .html(node.name)
               .style("left", event.pageX + "px")
               .style("top", event.pageY + "px");
    }
    
    private mouseOverLegend(event, node) {
        this.Tooltip.style("opacity", 1)
               .html("One degree of separation between books means readers who read one book mostly also read the other")
               .style("left", event.pageX + "px")
               .style("top", event.pageY + "px");
    }
    
    private mouseOut(event, node) {
        this.Tooltip.style("opacity", 0).style("left", -100 + "px").style("top", -100 + "px");
    }

    private initialize(){
        if(this.hasBeenInitialized) { return; }
        
        const width = this.root.offsetWidth
        const height = this.root.offsetHeight

        const d3 = (window as any).d3;
        
        const svg = d3.select(this.root.querySelector(".graph"));
        svg.attr('width', width).attr('height', height)
        
        // simulation setup with all forces
        const linkForce = d3.forceLink().id(link => link.id).strength(link => 0.7)
        
        this.simulation = d3.forceSimulation()
                            .force('link', linkForce)
                            .force('charge', d3.forceManyBody().strength(-50))
                            //.force('radial', d3.forceRadial(d => d.level * 100, width / 2, height / 2))
                            //.force('center', d3.forceCenter(width / 2, height / 2))
        
        this.dragDrop = d3.drag().on('start', (event, node) => {
            node.fx = node.x
            node.fy = node.y
        }).on('drag', (event, node) => {
            this.simulation.alphaTarget(0.7).restart()
            node.fx = event.x
            node.fy = event.y
        }).on('end', (event, node) => {
            if (!event.active) {
                this.simulation.alphaTarget(0);
            }
            
            node.fx = null;
            node.fy = null;
        });
        
        // create a tooltip
        this.Tooltip = d3.select(document.body)
                         .append("div")
                         .style("opacity", 0)
                         .attr("class", "graph-tooltip")
                         .style("background-color", "white")
                         .style("border", "solid")
                         .style("border-width", "2px")
                         .style("border-radius", "5px")
                         .style("position", "absolute")
                         .style("padding", "5px");
        
        const legendItems = d3.selectAll(this.root.querySelectorAll(".legend-degree"));
        legendItems.on("mouseover", this.mouseOverLegend);
        legendItems.on("mouseout", this.mouseOut);

        this.hasBeenInitialized = true;
    }

    private defaultCenterNodeX: number = 50;
    private defaultCenterNodeY: number = 30;

    private startSimulationWithJson(json, doRestart) {
        this.initialize();
        
        const d3 = (window as any).d3;
        const width = this.root.offsetWidth;
        const height = this.root.offsetHeight;
        
        const svg = d3.select(this.root.querySelector(".graph"));
        if (json == null || (!doRestart && json.nodes.length < 5)) {
            console.log("Should remove graph");
            d3.select("#legend").style("display", "none");
            d3.select("#customers-also-reading").style("display", "none");
            return;
        }
            
        const nodes = json.nodes;
        const links = json.links;

        nodes.forEach(node => {
            if(node.level == 0){
                node.x = this.defaultCenterNodeX;
                node.y = this.defaultCenterNodeY;
            }
        });
    
        if (doRestart) {
            svg.selectAll("*").remove();
        }
    
        const linkElements = svg.append("g")
            .attr("class", "links")
            .selectAll("line")
            .data(links)
            .enter().append("line")
            .attr("stroke-width", link => 1)
            .attr("stroke", "rgba(50, 50, 50, 0.2)");
    
        const nodeElements = svg.append("g")
            .attr("class", "nodes")
            .selectAll("circle")
            .data(nodes)
            .enter().append("circle")
            .attr("r", 5)
            .attr("fill", this.getNodeColor)
            .call(this.dragDrop)
            .on("mouseover", this.mouseOver)
            .on("mouseout", this.mouseOut)
            .on('click', (e, d) => {
                this.props.onProductDetailsRequested && this.props.onProductDetailsRequested(d.bookId);
            });

        var text = svg.append("g")
                      .attr("class", "labels")
                      .selectAll("text")
                      .data(nodes)
                      .enter().append("text").filter(d => d.level == 0)
                      .attr("text-anchor", d => {
                          return this.defaultCenterNodeX <= 70 
                               ? "start" 
                               : this.defaultCenterNodeX >= 270 ? "end" : "middle"
                      })
                      .attr("dx", d => {
                        return this.defaultCenterNodeX <= 70 
                             ? 10 
                             : this.defaultCenterNodeX >= 270 ? this.defaultCenterNodeX + 20 : this.defaultCenterNodeX;
                      })
                      .attr("dy", this.defaultCenterNodeY - 15)
                      .attr("title", this.props.productTitle)
                      .text(d => {
                          let title = this.props.productTitle;

                          if(title && title.length > 25){
                              title = title.substr(0, 25) + "...";
                          }

                          return title || "";
                      });
    
        this.simulation.nodes(nodes).on('tick', () => {
            //Limit the size based on the parent element
            nodeElements.attr("cx", d => {
                if(d.level == 0){
                    return d.x = this.defaultCenterNodeX;
                }
                else {
                    return d.x = Math.max(20, Math.min(width-20, d.x)); 
                }
            }).attr("cy", d => {
                if(d.level == 0){
                    return d.y = this.defaultCenterNodeY;
                }
                else {
                    return d.y = Math.max(40, Math.min(height-20, d.y)); 
                }
            });
            
            linkElements
                .attr('x1', link => link.source.x)
                .attr('y1', link => link.source.y)
                .attr('x2', link => link.target.x)
                .attr('y2', link => link.target.y)
        });
    
        this.simulation.force("link").links(links);
    
        if (doRestart) {
            this.simulation.alpha(1);
            this.simulation.restart();
        }
    }

    private loadSimulation(productId, doRestart) {
        WebUtils.getS3FileContent(`https://lb-recommender.s3-us-west-2.amazonaws.com/${productId}-strong.json`, content => {
            try {
                this.startSimulationWithJson(JSON.parse(content), doRestart);
                this.props.onGraphLoaded();
            }
            catch(e){
                console.log("Error when loading graph JSON", e);
                this.props.onGraphFailedToLoad && this.props.onGraphFailedToLoad();
            }
        }, error => {
            console.log("Error loading graph")
        });
    }
}